import axios from "axios";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { store } from "./store/store";
import { clearUser } from "./store/userSlice";
import { routes } from "./routes/routes";
import { endpoints } from "./api/endpoints";
import { isGuestRoleRequiredError, requestGuestConversionModal } from "./constants/guestRestrictions";

export let cachedToken: string | null = null;

export const clearCachedAuthToken = () => {
    cachedToken = null;
};

export const resetCachedToken = clearCachedAuthToken;

const updateLastActive = () => {
    localStorage.setItem('lastActive', Date.now().toString());
};

function isIntakeRoute(): boolean {
    return window.location.pathname === routes.INTAKE;
}

function isExternalApiUrl(url?: string): boolean {
    if (!url) return false;
    return url.includes("googleapis.com");
}

export const setupAxios = () => {
    axios.interceptors.request.use(
        async function (config) {
            if (isExternalApiUrl(config.url)) {
                config.withCredentials = false;
                return config;
            }

            updateLastActive();

            config.withCredentials = true;

            const existingAuth =
                config.headers?.Authorization ?? config.headers?.authorization;
            if (existingAuth) {
                return config;
            }

            const auth = getAuth();

            if (cachedToken) {
                config.headers.Authorization = `Bearer ${cachedToken}`;
                return config;
            }

            return new Promise((resolve) => {
                onAuthStateChanged(auth, async (user) => {
                    if (user) {
                        try {
                            cachedToken = await user.getIdToken();
                            config.headers.Authorization = `Bearer ${cachedToken}`;
                        } catch (error) {
                            console.error("Fail getting token from Firebase", error);
                        }
                    } else {
                        cachedToken = null;
                    }
                    resolve(config);
                });
            });
        },
        function (error) {
            return Promise.reject(error);
        }
    );

    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            const requestUrl = error.response?.config?.url;
            if (error.response?.status === 401) {
                if (requestUrl?.includes(endpoints.whoami) || isIntakeRoute()) {
                    return Promise.reject(error);
                }
            const auth = getAuth();
            resetCachedToken();
            signOut(auth).then(() => {
                store.dispatch(clearUser());
                localStorage.removeItem('lastActive');
                window.location.href = routes.LOGIN;
            }).catch((logoutError) => {
                console.error("Error during 401 logout:", logoutError);
                store.dispatch(clearUser());
                localStorage.removeItem('lastActive');
                window.location.href = routes.LOGIN;
            });

                return Promise.reject("Unauthorized: Redirected to Login");
            }

            if (isGuestRoleRequiredError(error)) {
                requestGuestConversionModal();
            }

            return Promise.reject(error);
        }
    );
};
